import { Assert } from '@cotera/utilities';
import { useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Loading, ModalPageView, Text } from '@cotera/client/app/components';
import { Ty } from '@cotera/era';
import { FieldError } from '../../components/ui/field';
import { useInScopeModel, useWatchableValue } from '../../actions/framework';
import { CsvParseError, CsvUploadModel } from './csv-upload.model';
import { cn } from '../../lib/utils';
import React from 'react';

export const MAX_CSV_DATA_ROWS = 10_000;

const ERROR_MESSAGES: Record<CsvParseError['t'], string> = {
  'empty-csv-file': 'CSV file is empty',
  'exceeded-max-rows': `CSV file exceeds maximum supported rows (${MAX_CSV_DATA_ROWS})`,
  'create-table-error':
    'Failed to create table. Please try again or contact support.',
  'parse-error': 'Failed to parse CSV file',
  'staging-error': 'Failed to stage dataset operations. Please try again.',
} as const;

export const CsvUploadModal: React.FC<{
  onContinue?: () => Promise<void>;
}> = ({ onContinue }) => {
  const model = useInScopeModel(CsvUploadModel);
  const state = useWatchableValue(model.state);

  return (
    <ModalPageView
      title="New Dataset from CSV"
      subtitle="Create a dataset from a CSV file."
      className="px-4 py-4"
    >
      {state.t === 'await-upload' ? (
        <CSVUploadForm onContinue={onContinue} label="Select File" />
      ) : state.t === 'parsing-file' ? (
        <ParseFile file={state.file} />
      ) : null}
    </ModalPageView>
  );
};

export const CSVUploadForm: React.FC<{
  label: string;
  className?: string;
  onContinue?: () => Promise<void>;
}> = ({ onContinue }) => {
  const model = useInScopeModel(CsvUploadModel);
  const tentativeFile = useWatchableValue<File | null>(model.tentativeFile);
  const inputRef = useRef<HTMLInputElement>(null);
  const modelState = useWatchableValue(model.state);
  const errors =
    modelState.t === 'await-upload' && modelState.errors
      ? modelState.errors
      : ([] as CsvParseError[]);

  return (
    <>
      <div
        className={cn(
          tentativeFile === null ? 'flex flex-col gap-4' : 'hidden'
        )}
      >
        <DropZone
          accept={['text/csv']}
          onChange={async (files) => {
            const file = files.item(0);
            if (file) {
              model.tentativeFile.set(file);
            }
          }}
        />
      </div>
      {tentativeFile !== null && (
        <div className="space-y-4">
          <div className="mb-2 flex items-center gap-2">
            <Text.Caption>Selected File:</Text.Caption>
            <code className="text-red-900 bg-amber-50">
              {tentativeFile.name}
            </code>
            <Button
              variant="ghost-destructive"
              size="icon"
              icon="trash"
              onClick={() => {
                model.tentativeFile.set(null);
                inputRef.current?.click();
              }}
            />
          </div>
          <FieldError
            className="mb-2"
            errors={errors.map((e) => ({ message: ERROR_MESSAGES[e.t] }))}
          />
          <div className="mb-2 flex gap-2 justify-end">
            <Button onClick={onContinue}>Continue</Button>
          </div>
        </div>
      )}
    </>
  );
};

const ParseFile: React.FC<{
  file: File;
}> = ({ file }) => {
  return (
    <div className="flex flex items-center space-x-2">
      <Text.Caption>Parsing File: {file.name}</Text.Caption>
      <Loading.Dots />
    </div>
  );
};

export const deserialize = (rawDataRows: string[][]): Ty.Scalar[][] => {
  const rawColumns = transpose(rawDataRows);
  const columns = rawColumns.map(tryParse);
  return transpose(columns);
};

/**
 * Given a series of possibly serialized strings, if all of them are serialized, then
 * returns the array of corresponding scalars; else return the original list of values.
 *
 * Also recognizes the convention that an empty string represents null.
 */
const tryParse = (rawValues: string[]): Ty.Scalar[] => {
  try {
    return rawValues.map((val) => {
      if (val === '') {
        return null;
      }
      return JSON.parse(val);
    });
  } catch (e) {
    return rawValues;
  }
};

/**
 * Transposes a M x N array to a N x M one.
 */
const transpose = <T,>(array: T[][]): T[][] => {
  if (array.length === 0) {
    return [];
  }

  const numColumns = Assert.assertExists(array[0]).length;

  return [...Array(numColumns)].map((_, colNum) =>
    array.map((row) => row[colNum] as T)
  );
};

const DropZone: React.FC<{
  onChange: (files: FileList) => void;
  accept: string[];
}> = ({ onChange, accept = ['*'] }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <div
      className={cn(
        'flex w-full border border-dashed rounded px-4 py-2 cursor-pointer transition-all h-[200px]',
        isDragging
          ? 'bg-gray-100 border-gray-400'
          : 'bg-gray-50 border-gray-300'
      )}
    >
      <Banner
        onClick={handleClick}
        onDrop={(files) => {
          onChange(files);
        }}
        onDrag={(isDragging) => {
          setIsDragging(isDragging);
        }}
      />
      <input
        type="file"
        aria-label="add files"
        className={'hidden'}
        ref={inputRef}
        onChange={(ev) => {
          if (ev.target.files) {
            onChange(ev.target.files);
          }
        }}
        accept={accept.join(',')}
      />
    </div>
  );
};

const Banner: React.FC<{
  onClick: () => void;
  onDrop: (files: FileList) => void;
  onDrag: (isDragging: boolean) => void;
}> = ({ onClick, onDrop, onDrag }) => {
  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (ev) => {
    onDrag(true);
    ev.preventDefault();
    ev.stopPropagation();
    ev.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    onDrop(ev.dataTransfer.files);
    onDrag(false);
  };

  return (
    <div
      className={cn(
        'flex justify-center h-full items-center w-full text-standard-text'
      )}
      onClick={onClick}
      onDragOver={handleDragOver}
      onDragLeave={() => {
        onDrag(false);
      }}
      onDrop={handleDrop}
    >
      <span className="text-sm font-medium text-standard-text">
        Click or Drag to Add files
      </span>
    </div>
  );
};
